import { useCallback, useEffect, useState } from 'react';
import { CrmApi } from '../../api/resources';
import { errorMessage, validationErrors } from '../../api/client';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';
import { StatusBadge } from './useCrmLookups';

function StatusModal({ status, onClose, onSaved }) {
  const editing = !!status;
  const [form, setForm] = useState({
    name: status?.name || '',
    color: status?.color || '#64748b',
    is_default: status?.is_default || false,
    is_closed: status?.is_closed || false,
    sort_order: status?.sort_order ?? '',
  });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    const payload = { ...form, sort_order: form.sort_order === '' ? null : Number(form.sort_order) };
    try {
      if (editing) await CrmApi.statuses.update(status.id, payload);
      else await CrmApi.statuses.create(payload);
      onSaved();
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not save status.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit ${status.name}` : 'New status'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <form onSubmit={submit}>
        <div className="field">
          <label>Name</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          {errors.name && <div className="err">{errors.name}</div>}
        </div>
        <div className="field">
          <label>Colour</label>
          <input
            type="color"
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
          />
          {errors.color && <div className="err">{errors.color}</div>}
        </div>
        <div className="field">
          <label>Order</label>
          <input
            type="number"
            min="0"
            placeholder="Last"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
          />
        </div>
        <div className="field">
          <label className="check">
            <input
              type="checkbox"
              checked={form.is_default}
              disabled={status?.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            />
            Default for new leads
          </label>
        </div>
        <div className="field">
          <label className="check">
            <input
              type="checkbox"
              checked={form.is_closed}
              onChange={(e) => setForm({ ...form, is_closed: e.target.checked })}
            />
            Closed (lead is finished, e.g. Won / Lost)
          </label>
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

export default function StatusesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit'|'delete', status }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await CrmApi.statuses.list());
    } catch (err) {
      setError(errorMessage(err, 'Could not load statuses.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterSave = () => {
    setModal(null);
    load();
  };

  const doDelete = async () => {
    try {
      await CrmApi.statuses.remove(modal.status.id);
      setModal(null);
      load();
    } catch (err) {
      setModal(null);
      setError(errorMessage(err, 'Could not delete status.'));
    }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Lead statuses</h1>
          <p className="page-sub">The stages a lead moves through, in the order shown in dropdowns.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>
          <Icon name="plus" size={16} />
          New status
        </button>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Order</th>
                <th>Status</th>
                <th>Flags</th>
                <th>Leads</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                rows.map((s) => (
                  <tr key={s.id}>
                    <td>{s.sort_order}</td>
                    <td><StatusBadge status={s} /></td>
                    <td>
                      {s.is_default && <span className="badge badge-ok">Default</span>}
                      {s.is_closed && <span className="badge badge-plain">Closed</span>}
                      {!s.is_default && !s.is_closed && <span className="muted">—</span>}
                    </td>
                    <td>{s.leads_count}</td>
                    <td>
                      <div className="row-actions">
                        <button className="icon-btn" title="Edit" aria-label={`Edit ${s.name}`}
                          onClick={() => setModal({ mode: 'edit', status: s })}>
                          <Icon name="edit" />
                        </button>
                        {!s.is_default && s.leads_count === 0 && (
                          <button className="icon-btn danger" title="Delete" aria-label={`Delete ${s.name}`}
                            onClick={() => setModal({ mode: 'delete', status: s })}>
                            <Icon name="trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal?.mode === 'create' && <StatusModal onClose={() => setModal(null)} onSaved={afterSave} />}
      {modal?.mode === 'edit' && (
        <StatusModal status={modal.status} onClose={() => setModal(null)} onSaved={afterSave} />
      )}
      {modal?.mode === 'delete' && (
        <ConfirmModal
          title="Delete status"
          message={`Delete “${modal.status.name}”?`}
          onConfirm={doDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
